import { Autocomplete, Checkbox, TextField, Typography } from '@mui/material';
import { useSearchkit } from '@searchkit/client';
import React, { useEffect, useMemo, useState } from 'react';
import { SkFacetSet, SkFacetSetEntry } from 'sr-types/lib/search/v1/graphql';
import { V } from '../../../common/Layout';
import { useI18n } from '../../../common/i18n/I18n';
import { Icons } from '../../../common/icons/Icons';

const icon = <Icons.CheckboxBlank fontSize="small" />;
const checkedIcon = <Icons.Checkbox fontSize="small" />;

type CustomComboBoxFacetProps = {
    facet: SkFacetSet;
    autoApply?: boolean;
    onFiltersChanged?: () => void;
};

export const CustomComboBoxFacet = ({ facet, autoApply, onFiltersChanged }: CustomComboBoxFacetProps) => {
    const api = useSearchkit();
    const options = useMemo(
        () => facet.entries.map((entry: SkFacetSetEntry) => ({ label: entry.label, value: entry.id.split('_')[1] })),
        [facet.entries]
    );
    const [filters, setFilters] = useState(() =>
        api.getFiltersByIdentifier(facet.identifier).map((filter) => ({ label: filter.value }))
    );
    const placeholder = useI18n('filter.search', { label: facet.label });

    useEffect(() => {
        const apiFilters = api.getFiltersByIdentifier(facet.identifier);
        if (apiFilters.length === 0 && filters.length > 0) {
            setFilters([]);
            return;
        }

        const apiValueSet = apiFilters.map((f) => f.value);
        const localValueSet = filters.map((f) => f.label);

        const lengthsDiffer = apiFilters.length !== filters.length;
        const hasNewApiValues = apiValueSet.some((v) => !localValueSet.includes(v));
        const hasRemovedLocalValues = localValueSet.some((v) => !apiValueSet.includes(v));

        if (lengthsDiffer || hasNewApiValues || hasRemovedLocalValues) {
            setFilters(apiFilters.map((f) => ({ label: f.value })));
        }
    }, [api, facet.identifier, filters, facet.entries]);

    const handleChange = (event: React.SyntheticEvent, newFilters: { label: string }[]) => {
        setFilters(newFilters);
        const pageSize = api.getSearchState().page.size;
        api.removeFiltersByIdentifier(facet.identifier);
        newFilters.forEach((f) => {
            api.addFilter({ identifier: facet.identifier, value: f.label });
        });
        api.setPage({
            from: 0,
            size: pageSize
        });
        onFiltersChanged?.();
        if (autoApply) {
            api.search();
        }
    };

    return (
        <V>
            <Typography sx={{ fontSize: 'small' }}>{facet.label}</Typography>
            <Autocomplete
                multiple
                fullWidth
                disableCloseOnSelect
                size={'small'}
                isOptionEqualToValue={(option, value) => {
                    return option && value && option.label === value.label;
                }}
                options={[...options]}
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={props.id}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {option.label}
                    </li>
                )}
                renderInput={(params) => <TextField variant={'standard'} {...params} placeholder={placeholder} />}
                filterSelectedOptions
                value={[...filters]}
                onChange={handleChange}
            />
        </V>
    );
};

CustomComboBoxFacet.DISPLAY = 'CustomComboBoxFacet';
